"use client";

import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, List, ListOrdered, Strikethrough } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import "./mention.css";
import "./tiptap-editor.css";
import type { Editor } from "@tiptap/core";
import Emoji from "@tiptap/extension-emoji";
import Mention from "@tiptap/extension-mention";

import { forwardRef, useImperativeHandle, useRef } from "react";
import { OrsaneMediaNode } from "./OrsaneMediaNode";
import { mentionListConfiguration, mentionListExtension } from "./userSuggestion";

export interface ChatEditorProps {
    value?: Record<string, any> | null;
    onChange?: (json: Record<string, any>, html: string) => void;
    className?: string;
    onImageUpload?: (file: File) => Promise<string>;
    onPasteFile?: (file: File) => void;
}

export interface ChatEditorRef {
    editor: Editor | null;
}

export const ChatEditor = forwardRef<ChatEditorRef, ChatEditorProps>(({ value = "", onChange, className, onImageUpload, onPasteFile }, ref) => {
    const onPasteFileRef = useRef(onPasteFile);
    onPasteFileRef.current = onPasteFile;

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // The Collaboration extension comes with its own history handling
                undoRedo: false,
                blockquote: false,
                code: false,
                horizontalRule: false,
                link: {
                    autolink: false,
                },
            }),
            OrsaneMediaNode.configure(),
            Emoji.configure({
                enableEmoticons: true,
                suggestion: false,
            }),
            Mention.extend(mentionListExtension()).configure(mentionListConfiguration()),
        ],
        onCreate: ({ editor }) => {
            editor.commands.setContent(value);
        },
        onUpdate: ({ editor }) => {
            onChange?.(editor.getJSON(), editor.getHTML());
        },
        editorProps: {
            handlePaste: (_view, event, _slice) => {
                const items = event.clipboardData?.items;
                if (!items) return false;

                for (let i = 0; i < items.length; i++) {
                    const item = items[i];
                    if (item.type.indexOf("image") === 0) {
                        const file = item.getAsFile();
                        if (file) {
                            const filesize = Number((file.size / 1024 / 1024).toFixed(4));
                            if (filesize < 10) {
                                const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
                                const extension = file.type.split("/")[1] || "png";
                                const generatedFile = new File([file], `pasted-image-${timestamp}.${extension}`, {
                                    type: file.type,
                                });
                                onPasteFileRef.current?.(generatedFile);
                                event.preventDefault();
                                return true;
                            }
                        }
                    }
                }

                return false;
            },
        },
    });

    useImperativeHandle(ref, () => ({
        editor: editor,
    }));

    const insertMedia = async (file: File) => {
        if (!editor || !onImageUpload) return;

        // For now we'll use a local URL, but in production this should be replaced
        // with the actual uploaded image URL from onImageUpload
        const url = URL.createObjectURL(file);
        const fileName: string = file.name;

        const contentType = file.type;

        editor.commands.addOrsaneMedia({ url, fileName, contentType, id: "temp-id", href: undefined });
    };

    if (!editor) {
        return null;
    }

    // Make insertMedia available through the editor instance
    (editor as any).insertMedia = insertMedia;

    return (
        <>
            {editor && (
                <BubbleMenu updateDelay={200} className="bg-white rounded-lg p-1 shadow border border-neutral-200 flex gap-0.5" editor={editor}>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor.isActive("bold") }])}
                    >
                        <Bold className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor.isActive("italic") }])}
                    >
                        <Italic className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        className={cn(["h-8 w-8", { "bg-neutral-100": editor.isActive("strike") }])}
                    >
                        <Strikethrough className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        className={cn([
                            "h-8 w-8",
                            {
                                "bg-neutral-100": editor.isActive("bulletList"),
                            },
                        ])}
                        aria-label="Toggle bullet list"
                    >
                        <List className="h-4 w-4" />
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        className={cn([
                            "h-8 w-8",
                            {
                                "bg-neutral-100": editor.isActive("orderedList"),
                            },
                        ])}
                        aria-label="Toggle ordered list"
                    >
                        <ListOrdered className="h-4 w-4" />
                    </Button>
                </BubbleMenu>
            )}
            <EditorContent editor={editor} className={cn("comment-editor taskform-editor max-w-none focus-visible:outline-none ", className)} />
        </>
    );
});

ChatEditor.displayName = "ChatEditor";

export default ChatEditor;
